import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, Send, AlertTriangle, Eye, FileDown, History, Mail, Landmark, FileText, ClipboardCopy, Download, CheckCircle2, XCircle } from 'lucide-react';

// EZYtransfer 63995800 — the STP-capable MINT source account (matches backend SOURCE_ACCOUNT_ID).
const EZYTRANSFER_MINT_ACCOUNT_ID = '3919226411278897070';
// Filter the live MINT statement so each tab shows only its own external wires.
const PROVIDER_MATCH = {
  TKM: (desc) => /team km|ek investment|tkm/i.test(desc),
  Bridge: (desc) => /bridge|bullaale/i.test(desc),
};

const SWIFT_ROWS = [
  ['Status', 'status'],
  ['SWIFT reference (F20)', 'swift_reference'],
  ['Message type', 'fin_message'],
  ['UETR (gpi)', 'uetr'],
  ['Value date / amount (F32A)', 'value_date_amount'],
  ['Charges (F71A)', 'charges'],
  ['Instruction (F23E)', 'instruction_code'],
  ['Payer FI (F52A)', 'payer_fi'],
  ['USD Mint LEI', 'lei'],
  ['Beneficiary', 'beneficiary_name'],
  ['Beneficiary account', 'beneficiary_account'],
  ['Beneficiary FI / routing', 'beneficiary_fi_routing'],
];

// Authenticated blob download of a PDF from an admin endpoint (needs a Bearer token).
const downloadPdf = async (path, filename, errMsg) => {
  try {
    const res = await api.get(path, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(errMsg || 'Could not download PDF');
  }
};

const downloadConfirmationPdf = (txNumber, internalId) => {
  if (!txNumber) { toast.error('Missing transaction number'); return; }
  const q = internalId ? `?internal_id=${internalId}` : '';
  return downloadPdf(
    `/usdmint/account/${EZYTRANSFER_MINT_ACCOUNT_ID}/transaction/${txNumber}/confirmation.pdf${q}`,
    `MINT_Confirmation_${txNumber}.pdf`,
    'Could not download confirmation PDF',
  );
};

const downloadSwiftAdvice = (txNumber, internalId) => {
  if (!txNumber) { toast.error('Missing transaction number'); return; }
  const q = internalId ? `?internal_id=${internalId}` : '';
  return downloadPdf(
    `/usdmint/account/${EZYTRANSFER_MINT_ACCOUNT_ID}/transaction/${txNumber}/swift-advice.pdf${q}`,
    `SWIFT_Advice_${txNumber}.pdf`,
    'Could not download SWIFT advice',
  );
};

// Plain-text download (e.g. the MT103 block saved as a .txt file).
const downloadText = (content, filename) => {
  try {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    toast.error('Could not download file');
  }
};

// Mandatory MT103 (block 4) fields per the SWIFT MT103 standard. We match the tag
// variants the block actually emits (:50K:, :59:, etc.) and capture the value line.
const MT103_MANDATORY = [
  { tag: '20', label: 'Sender\u2019s Reference', re: /^:20:(.*)$/m },
  { tag: '23B', label: 'Bank Operation Code', re: /^:23B:(.*)$/m },
  { tag: '32A', label: 'Value Date / Ccy / Amount', re: /^:32A:(.*)$/m },
  { tag: '50a', label: 'Ordering Customer', re: /^:50[A-K]:(.*)$/m },
  { tag: '59a', label: 'Beneficiary Customer', re: /^:59[A-F]?:(.*)$/m },
  { tag: '71A', label: 'Details of Charges', re: /^:71A:(.*)$/m },
];

// Validate a reconstructed MT103 block for presence of every mandatory field.
const validateMt103 = (block) => {
  if (!block) return null;
  const fields = MT103_MANDATORY.map((f) => {
    const m = block.match(f.re);
    return { tag: f.tag, label: f.label, present: !!m, value: m ? (m[1] || '').trim() : '' };
  });
  const missing = fields.filter((f) => !f.present);
  return { fields, missing, ok: missing.length === 0 };
};

// One external-wire row: confirmation PDF, live SWIFT details, and one-click email receipt.
const WireRow = ({ row, provider }) => {
  const tag = provider.toLowerCase();
  const tx = row.transaction_number;
  const internalId = row.id;
  const [swift, setSwift] = useState(null);
  const [showSwift, setShowSwift] = useState(false);
  const [loadingSwift, setLoadingSwift] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);

  const mt103Validation = useMemo(() => validateMt103(swift?.mt103_block), [swift]);

  const toggleSwift = async () => {
    if (swift) { setShowSwift((s) => !s); return; }
    setLoadingSwift(true);
    try {
      const q = internalId ? `?internal_id=${internalId}` : '';
      const res = await api.get(`/usdmint/account/${EZYTRANSFER_MINT_ACCOUNT_ID}/transaction/${tx}/swift-details${q}`);
      setSwift(res.data); setShowSwift(true);
    } catch (e) {
      toast.error('Could not load SWIFT details');
    } finally { setLoadingSwift(false); }
  };

  const sendEmail = async () => {
    if (!email || !email.includes('@')) { toast.error('Enter a valid email'); return; }
    setSending(true);
    try {
      const res = await api.post(`/usdmint/account/${EZYTRANSFER_MINT_ACCOUNT_ID}/transaction/${tx}/email-confirmation`, { to_email: email, doc_type: 'swift_advice', internal_id: internalId });
      toast.success(`SWIFT advice emailed to ${res.data.to}`);
      setShowEmail(false); setEmail('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Email failed');
    } finally { setSending(false); }
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2" data-testid={`ext-stp-${tag}-history-row`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-slate-800 truncate">
            USD {Number(row.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })} · <span className="font-mono">{tx}</span>
          </p>
          <p className="text-[11px] text-slate-500 truncate">{row.description}</p>
          <p className="text-[10px] text-slate-400">{row.date ? new Date(row.date).toLocaleString() : ''}</p>
        </div>
        <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
          <Button variant="outline" size="sm" onClick={() => downloadSwiftAdvice(tx, internalId)} data-testid={`ext-stp-${tag}-history-swift-pdf`}>
            <FileText className="w-3 h-3 mr-1" /> SWIFT Receipt
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadConfirmationPdf(tx, internalId)} data-testid={`ext-stp-${tag}-history-pdf`}>
            <FileDown className="w-3 h-3 mr-1" /> Confirmation
          </Button>
          <Button variant="outline" size="sm" onClick={toggleSwift} disabled={loadingSwift} data-testid={`ext-stp-${tag}-history-swift`}>
            {loadingSwift ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Landmark className="w-3 h-3 mr-1" />} Details
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowEmail((s) => !s)} data-testid={`ext-stp-${tag}-history-email-btn`}>
            <Mail className="w-3 h-3 mr-1" /> Email
          </Button>
        </div>
      </div>

      {showSwift && swift && (
        <div className="mt-2 rounded-md border border-sky-200 bg-sky-50/60 p-3" data-testid={`ext-stp-${tag}-swift-panel`}>
          <p className="text-[11px] font-semibold text-sky-800 mb-1">SWIFT / FIN message (live from MINT)</p>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
            {SWIFT_ROWS.map(([label, k]) => (swift[k] ? (
              <React.Fragment key={k}>
                <span className="text-slate-500">{label}</span>
                <span className="font-mono text-slate-800 break-all">{String(swift[k])}</span>
              </React.Fragment>
            ) : null))}
          </div>
          <p className="text-[10px] text-slate-500 mt-1">{swift.uetr_note}</p>
          {swift.mt103_block && (
            <div className="mt-2">
              <div className="flex items-center justify-between mb-1">
                <p className="text-[11px] font-semibold text-sky-800">MT103 block format</p>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]"
                    onClick={() => { navigator.clipboard?.writeText(swift.mt103_block); toast.success('MT103 copied'); }}
                    data-testid={`ext-stp-${tag}-mt103-copy`}>
                    <ClipboardCopy className="w-3 h-3 mr-1" /> Copy
                  </Button>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]"
                    onClick={() => downloadText(swift.mt103_block, `MT103_${tx}.txt`)}
                    data-testid={`ext-stp-${tag}-mt103-txt`}>
                    <Download className="w-3 h-3 mr-1" /> .txt
                  </Button>
                </div>
              </div>
              <pre className="text-[10px] font-mono bg-slate-900 text-slate-100 rounded-md p-2 overflow-x-auto whitespace-pre" data-testid={`ext-stp-${tag}-mt103`}>{swift.mt103_block}</pre>
              {mt103Validation && (
                <div className="mt-2 rounded-md border border-slate-200 bg-white p-2" data-testid={`ext-stp-${tag}-mt103-validation`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[11px] font-semibold text-slate-700">Mandatory field check</p>
                    {mt103Validation.ok ? (
                      <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]" data-testid={`ext-stp-${tag}-mt103-valid-badge`}>
                        <CheckCircle2 className="w-3 h-3 mr-1" /> All present
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-[10px]" data-testid={`ext-stp-${tag}-mt103-invalid-badge`}>
                        <AlertTriangle className="w-3 h-3 mr-1" /> {mt103Validation.missing.length} missing
                      </Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5">
                    {mt103Validation.fields.map((f) => (
                      <div key={f.tag} className="flex items-center gap-1 text-[10px]" data-testid={`ext-stp-${tag}-mt103-field-${f.tag}`} title={f.value}>
                        {f.present
                          ? <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
                          : <XCircle className="w-3 h-3 text-rose-500 shrink-0" />}
                        <span className="font-mono text-slate-500">:{f.tag}:</span>
                        <span className="text-slate-600 truncate">{f.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {showEmail && (
        <div className="mt-2 flex items-center gap-2" data-testid={`ext-stp-${tag}-email-row`}>
          <Input type="email" placeholder="recipient@bank.com" value={email}
            onChange={(e) => setEmail(e.target.value)} className="h-8 text-xs" data-testid={`ext-stp-${tag}-email-input`} />
          <Button size="sm" onClick={sendEmail} disabled={sending} data-testid={`ext-stp-${tag}-email-send`}>
            {sending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />} Send
          </Button>
        </div>
      )}
    </div>
  );
};

const PREFILLS = {
  TKM: {
    beneficiary_mode: 'iban',
    beneficiary_name: 'EK Investment & Management GmbH',
    iban: 'LT163250088797860492',
    bic: 'REVOLT21',
    account_number: '',
    routing_number: '',
  },
  Bridge: {
    beneficiary_mode: 'account',
    beneficiary_name: 'Bullaale AS',
    iban: '',
    bic: '',
    account_number: '214088326179',
    routing_number: '101019644',
  },
};

// Warn-only sanction-screening banner (never blocks the wire).
const ScreeningWarn = ({ screening, testid }) => {
  if (!screening || !screening.hit) return null;
  return (
    <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3" data-testid={testid}>
      <p className="text-xs font-semibold text-amber-800 flex items-center gap-1">
        <AlertTriangle className="w-4 h-4" /> Sanction/Watchlist match — review before proceeding (warn-only, not blocked)
      </p>
      <ul className="mt-1 space-y-0.5">
        {(screening.matches || []).map((m, i) => (
          <li key={i} className="text-[11px] text-amber-900 font-mono">
            “{m.screened_name}” ↔ {m.watchlist_name} · {m.reason} · {m.risk_level}
          </li>
        ))}
      </ul>
    </div>
  );
};


const ExternalStpSettlement = ({ provider = 'Other' }) => {
  const base = PREFILLS[provider] || { beneficiary_mode: 'iban', beneficiary_name: '', iban: '', bic: '', account_number: '', routing_number: '' };
  const [form, setForm] = useState({
    amount: '', reference: '', description: '', provider_tag: provider,
    rail: 'pacs8',
    creditor_agent_name: provider === 'TKM' ? 'Revolut' : (provider === 'Bridge' ? 'Bullaale Bank' : ''),
    ...base,
  });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  // Persistent history pulled LIVE from the MINT statement (works on production —
  // no DB dependency). Filtered to this account's external "EZYtransfer STP" outflows.
  const loadHistory = useCallback(async () => {
    setHistLoading(true);
    try {
      const res = await api.get(`/usdmint/account/${EZYTRANSFER_MINT_ACCOUNT_ID}/statement?limit=200`);
      const rows = (res.data?.rows || []).filter(
        (r) => r.direction === 'outflow' && /ezytransfer\s*(stp|pacs8)/i.test(String(r.type || ''))
      );
      const match = PROVIDER_MATCH[provider];
      setHistory(match ? rows.filter((r) => match(r.description || '')) : rows);
    } catch (e) {
      setHistory([]);
    } finally {
      setHistLoading(false);
    }
  }, [provider]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Any edit invalidates a prior preview (prevents executing a stale preview)
  const update = (patch) => { setForm((p) => ({ ...p, ...patch })); setPreview(null); setConfirming(false); };

  const payload = () => ({
    amount: parseFloat(form.amount) || 0,
    beneficiary_name: form.beneficiary_name,
    beneficiary_mode: form.beneficiary_mode,
    iban: form.iban,
    bic: form.bic,
    account_number: form.account_number,
    routing_number: form.routing_number,
    reference: form.reference,
    description: form.description,
    provider_tag: form.provider_tag,
    rail: form.rail,
    creditor_agent_name: form.creditor_agent_name,
  });

  const doPreview = async () => {
    if (!(parseFloat(form.amount) > 0)) { toast.error('Enter an amount > 0'); return; }
    setBusy(true); setPreview(null); setConfirming(false);
    try {
      const res = await api.post('/mint/external-settlement/preview', payload());
      if (res.data.valid) { setPreview(res.data); toast.success('Preview validated — review and confirm'); }
      else { toast.error('Preview rejected by MINT'); setPreview(res.data); }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally { setBusy(false); }
  };

  const doExecute = async () => {
    setBusy(true);
    try {
      const res = await api.post('/mint/external-settlement/execute', { ...payload(), confirm: true });
      setLastResult(res.data);
      setPreview(null); setConfirming(false);
      setForm((p) => ({ ...p, amount: '', reference: '', description: '' }));
      toast.success(`Sent USD ${res.data.amount} — MINT tx ${res.data.mint_transaction_number}`);
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Execution failed');
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-6" data-testid={`ext-stp-${provider.toLowerCase()}`}>
      <div className="flex items-center gap-3 mb-1">
        <div className="w-1 h-8 bg-emerald-600 rounded-full" />
        <h3 className="text-xl font-semibold">External Settlement — SWIFT/STP</h3>
        <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">MINT → external</Badge>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Real outbound wire from <span className="font-mono">EZYtransfer 63995800</span>. Default rail is <span className="font-semibold">ISO20022 pacs.008</span> (settles via USD Mint RTGS → beneficiary). Preview is required before sending.
      </p>

      <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`ext-stp-${provider.toLowerCase()}-rail`}>
        <Label className="text-xs">Settlement rail</Label>
        <div className="flex flex-wrap gap-2 mt-1">
          <Button type="button" size="sm" variant={form.rail === 'pacs8' ? 'default' : 'outline'}
            onClick={() => update({ rail: 'pacs8' })} data-testid={`ext-stp-${provider.toLowerCase()}-rail-pacs8`}>ISO20022 pacs.008 (settles)</Button>
          <Button type="button" size="sm" variant={form.rail === 'stp' ? 'default' : 'outline'}
            onClick={() => update({ rail: 'stp' })} data-testid={`ext-stp-${provider.toLowerCase()}-rail-stp`}>Legacy STP (MT/FIN)</Button>
        </div>
        <p className="text-[11px] text-slate-500 mt-1">
          {form.rail === 'pacs8'
            ? 'pacs.008.001.08 via USD Mint RTGS — actually delivers to the beneficiary (target status ACSC).'
            : 'MT-style FIN — internal hop to PSPFI only; does NOT externally settle (legacy).'}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Amount (USD)</Label>
          <Input data-testid={`ext-stp-${provider.toLowerCase()}-amount`} type="number" step="0.01" value={form.amount}
            onChange={(e) => update({ amount: e.target.value })} placeholder="0.00" />
        </div>
        <div className="space-y-2">
          <Label>Beneficiary type</Label>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant={form.beneficiary_mode === 'iban' ? 'default' : 'outline'}
              data-testid={`ext-stp-${provider.toLowerCase()}-mode-iban`} onClick={() => update({ beneficiary_mode: 'iban' })}>IBAN / BIC</Button>
            <Button type="button" size="sm" variant={form.beneficiary_mode === 'account' ? 'default' : 'outline'}
              data-testid={`ext-stp-${provider.toLowerCase()}-mode-account`} onClick={() => update({ beneficiary_mode: 'account' })}>Account / Routing</Button>
          </div>
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label>Beneficiary name</Label>
          <Input data-testid={`ext-stp-${provider.toLowerCase()}-name`} value={form.beneficiary_name}
            onChange={(e) => update({ beneficiary_name: e.target.value })} />
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label>Beneficiary bank — cdtrAgt <span className="text-slate-400">(optional)</span></Label>
          <Input data-testid={`ext-stp-${provider.toLowerCase()}-cdtragt`} value={form.creditor_agent_name}
            onChange={(e) => update({ creditor_agent_name: e.target.value })} placeholder="e.g. Revolut" />
        </div>
        {form.beneficiary_mode === 'iban' ? (
          <>
            <div className="space-y-2">
              <Label>IBAN</Label>
              <Input data-testid={`ext-stp-${provider.toLowerCase()}-iban`} value={form.iban}
                onChange={(e) => update({ iban: e.target.value })} className="font-mono" />
            </div>
            <div className="space-y-2">
              <Label>BIC / SWIFT</Label>
              <Input data-testid={`ext-stp-${provider.toLowerCase()}-bic`} value={form.bic}
                onChange={(e) => update({ bic: e.target.value })} className="font-mono" />
            </div>
          </>
        ) : (
          <>
            <div className="space-y-2">
              <Label>Account number</Label>
              <Input data-testid={`ext-stp-${provider.toLowerCase()}-account`} value={form.account_number}
                onChange={(e) => update({ account_number: e.target.value })} className="font-mono" />
            </div>
            <div className="space-y-2">
              <Label>Routing number</Label>
              <Input data-testid={`ext-stp-${provider.toLowerCase()}-routing`} value={form.routing_number}
                onChange={(e) => update({ routing_number: e.target.value })} className="font-mono" />
            </div>
          </>
        )}
        <div className="space-y-2">
          <Label>Reference (optional)</Label>
          <Input data-testid={`ext-stp-${provider.toLowerCase()}-ref`} value={form.reference}
            onChange={(e) => update({ reference: e.target.value })} placeholder="auto if blank" />
        </div>
        <div className="space-y-2">
          <Label>Description (optional)</Label>
          <Input data-testid={`ext-stp-${provider.toLowerCase()}-desc`} value={form.description}
            onChange={(e) => update({ description: e.target.value })} />
        </div>
      </div>

      <div className="flex items-center gap-2 mt-4">
        <Button variant="outline" onClick={doPreview} disabled={busy} data-testid={`ext-stp-${provider.toLowerCase()}-preview-btn`}>
          {busy && !confirming ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Eye className="w-4 h-4 mr-1" />} Preview
        </Button>
      </div>

      {/* Preview result + confirm */}
      {preview && (
        preview.valid ? (
          <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/60 p-4" data-testid={`ext-stp-${provider.toLowerCase()}-preview-result`}>
            <p className="text-sm font-semibold text-emerald-800 flex items-center gap-1 mb-2"><ShieldCheck className="w-4 h-4" /> Preview validated — no money moved yet</p>
            <div className="grid grid-cols-2 gap-y-1 text-xs">
              <span className="text-slate-500">From</span><span className="font-mono">{preview.from}</span>
              <span className="text-slate-500">Via</span><span className="font-mono">{preview.to}</span>
              <span className="text-slate-500">Amount</span><span className="font-mono font-semibold">USD {Number(preview.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              <span className="text-slate-500">Beneficiary</span><span className="font-mono">{preview.beneficiary_name}</span>
              <span className="text-slate-500">Destination</span><span className="font-mono">{preview.destination}</span>
              <span className="text-slate-500">BIC / Routing</span><span className="font-mono">{preview.bic_or_routing}</span>
              {preview.message_type && (<><span className="text-slate-500">Message</span><span className="font-mono">{preview.message_type}</span></>)}
              {preview.settlement_method && (<><span className="text-slate-500">Settlement</span><span className="font-mono">{preview.settlement_method}</span></>)}
              {preview.uetr && (<><span className="text-slate-500">UETR</span><span className="font-mono break-all">{preview.uetr}</span></>)}
            </div>
            <ScreeningWarn screening={preview.screening} testid={`ext-stp-${provider.toLowerCase()}-screening-warn`} />
            {!confirming ? (
              <Button className="mt-3 bg-rose-600 hover:bg-rose-700" onClick={() => setConfirming(true)} data-testid={`ext-stp-${provider.toLowerCase()}-arm-btn`}>
                <Send className="w-4 h-4 mr-1" /> Send real payment…
              </Button>
            ) : (
              <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-3">
                <p className="text-xs text-rose-800 flex items-center gap-1 mb-2"><AlertTriangle className="w-4 h-4" /> This sends REAL funds (USD {Number(preview.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}) to {preview.beneficiary_name}. Confirm?</p>
                <div className="flex gap-2">
                  <Button className="bg-rose-600 hover:bg-rose-700" onClick={doExecute} disabled={busy} data-testid={`ext-stp-${provider.toLowerCase()}-confirm-btn`}>
                    {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Yes, send now
                  </Button>
                  <Button variant="outline" onClick={() => setConfirming(false)} disabled={busy}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50/60 p-3 text-xs text-rose-800" data-testid={`ext-stp-${provider.toLowerCase()}-preview-error`}>
            <p className="font-semibold flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> MINT rejected this payment (no money moved)</p>
            <p className="font-mono mt-1 break-all">{preview.error}</p>
            <ScreeningWarn screening={preview.screening} testid={`ext-stp-${provider.toLowerCase()}-screening-warn-invalid`} />
          </div>
        )
      )}

      {lastResult && (
        <div className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50/60 p-3 text-xs" data-testid={`ext-stp-${provider.toLowerCase()}-result`}>
          <p className="font-semibold text-indigo-800 mb-2">✓ Sent — MINT tx <span className="font-mono">{lastResult.mint_transaction_number}</span> · ref {lastResult.reference}</p>
          <Button variant="outline" size="sm" onClick={() => downloadConfirmationPdf(lastResult.mint_transaction_number)} data-testid={`ext-stp-${provider.toLowerCase()}-receipt`}>
            <FileDown className="w-3 h-3 mr-1" /> Download confirmation PDF
          </Button>
        </div>
      )}

      {/* Persistent history — live from the MINT rail (visible on production) */}
      <div className="mt-6 border-t pt-4" data-testid={`ext-stp-${provider.toLowerCase()}-history`}>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1">
            <History className="w-4 h-4 text-slate-500" /> Recent external wires (live from MINT)
          </h4>
          <Button variant="ghost" size="sm" onClick={loadHistory} disabled={histLoading} data-testid={`ext-stp-${provider.toLowerCase()}-history-refresh`}>
            {histLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Refresh'}
          </Button>
        </div>
        {history.length === 0 ? (
          <p className="text-xs text-slate-400">{histLoading ? 'Loading…' : 'No external wires found on the MINT rail yet.'}</p>
        ) : (
          <div className="space-y-2">
            {history.map((r) => (
              <WireRow key={r.transaction_number} row={r} provider={provider} />
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

export default ExternalStpSettlement;
